import { computed, inject } from '@angular/core';
import { initialState } from '@libs/core/lib/state/async-states';
import { Failure } from '@libs/core/lib/state/failure';
import {
  withCalculateShippingCost,
  withPlaceOrder,
} from '@libs/mtjr/shared/generated/entities/order/order-store-features';
import { Dtos, Entity, Enums } from '@libs/mtjr/shared/generated/types';
import { WalletStore } from '@libs/mtjr/shared/wallet/wallet.store';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { TranslateService } from '@ngx-translate/core';
import { v4 } from 'uuid';

export type CartItem = {
  product: Entity.Product;
  variation?: Entity.ProductVariation;
  quantity: number;
};

export type CartState = {
  items: CartItem[];
  address: Entity.UserAddress | undefined;
  checkoutFailure: Failure | undefined;
  paymentMethod: Enums.PaymentMethod | undefined;
  idempotencyKey: string;
};

const localStorageKey = 'mtjr-cart';

export function userAddressToAddress(address: Entity.UserAddress): Entity.Address {
  return {
    id: address.id,
    label: address.label,
    street: address.street,
    internalAddress: address.internalAddress,
    phone: address.phone,
    phone2: address.phone2,
    latitude: address.latitude,
    longitude: address.longitude,
  };
}

export const CartStore = signalStore(
  { providedIn: 'root' },

  withState<CartState>({
    items: [],
    address: undefined,
    checkoutFailure: undefined,
    paymentMethod: undefined,
    idempotencyKey: v4(),
  }),
  withPlaceOrder(),
  withCalculateShippingCost(),

  withComputed((store) => ({
    onlyDigitalProducts: computed(() => {
      const items = store.items();
      if (!items) return false;
      return items.every((item) => item.product.type === 'digital');
    }),
    totalCount: computed(() => store.items().reduce((acc, item) => acc + item.quantity, 0)),
    subTotal: computed(() =>
      store
        .items()
        .reduce(
          (acc, item) =>
            acc +
            ((item.variation?.price || item.product.price || 0) -
              (item.product.discount || item.variation?.discount || 0)) *
              item.quantity,
          0,
        ),
    ),
    totalDiscount: computed(() =>
      store
        .items()
        .reduce(
          (acc, item) =>
            acc +
            ((item.variation?.discount || item.product.discount || 0) -
              (item.product.discount || item.variation?.discount || 0)) *
              item.quantity,
          0,
        ),
    ),
    shippingCost: computed(() => {
      const state = store.calculateShippingCostState();
      return state.success ? state.data.cost : 0;
    }),
  })),
  withMethods((store, translate = inject(TranslateService), walletStore = inject(WalletStore)) => ({
    setPaymentMethod(method: Enums.PaymentMethod) {
      patchState(store, { paymentMethod: method, checkoutFailure: undefined });
    },

    async checkout() {
      const paymentMethod = store.paymentMethod();

      console.log(paymentMethod);

      if (paymentMethod === undefined) {
        patchState(store, {
          checkoutFailure: new Failure(
            translate.instant('CART.ERROR.PAYMENT_METHOD_REQUIRED'),
            'paymentMethod.checkout',
          ),
        });
        return;
      }
      const address = store.address();
      const hasNoneVirtualProduct = store.items().some((item) => item.product.vendor.vendorType === 'general');
      if (!address && hasNoneVirtualProduct) {
        patchState(store, {
          checkoutFailure: new Failure(translate.instant('CART.ERROR.ADDRESS_REQUIRED'), 'address.checkout'),
        });
        return;
      }

      const orderProducts: Dtos.PlaceOrderProduct[] = [];
      const idempotencyKeysPerVendor = store.items().reduce(
        (acc, item) => {
          if (!acc[item.product.vendor.id]) {
            acc[item.product.vendor.id] = v4();
          }
          return acc;
        },
        {} as Record<string, string>,
      );
      for (const item of store.items()) {
        const price = item.product.price ?? item.variation?.price;
        if (!price) {
          patchState(store, {
            checkoutFailure: new Failure(translate.instant('CART.ERROR.PRICE_REQUIRED'), 'price.checkout'),
          });
          return;
        }

        const discount = item.product.discount ?? item.variation?.discount ?? 0;
        const finalPrice = price - discount;
        if (!finalPrice) {
          patchState(store, {
            checkoutFailure: new Failure(translate.instant('CART.ERROR.PRICE_REQUIRED'), 'price.checkout'),
          });
          return;
        }

        orderProducts.push({
          productId: item.product.id,
          variationId: item.variation?.id ?? '000000000000000000000000',
          quantity: item.quantity,
          finalPrice: finalPrice,
          idempotencyKey: idempotencyKeysPerVendor[item.product.vendor.id],
        });
      }
      const paymentDue = store.subTotal() - store.totalDiscount();
      if (walletStore.walletBalance().lessThan(paymentDue) && paymentMethod === 'wallet') {
        patchState(store, {
          checkoutFailure: new Failure(translate.instant('CART.ERROR.INSUFFICIENT_WALLET_BALANCE'), 'wallet.checkout'),
        });
        return;
      }
      console.log(paymentMethod);
      return store.placeOrder({
        idempotencyKey: store.idempotencyKey(),
        address: address ? userAddressToAddress(address) : undefined,
        orderProducts: orderProducts,
        paymentMethod: paymentMethod,
      });
    },

    setAddress(address: Entity.UserAddress) {
      patchState(store, { address });
    },

    addItem(item: CartItem) {
      const items = store.items();
      const existingItemIndex = items.findIndex((x) => {
        if (!x.variation || !item.variation) return x.product.id === item.product.id;
        return x.product.id === item.product.id && x.variation.id === item.variation.id;
      });
      if (existingItemIndex >= 0) {
        this.increaseItemQuantity(existingItemIndex);
        return;
      }
      patchState(store, { items: [...items, item] });
      localStorage.setItem(localStorageKey, JSON.stringify(store.items()));
    },

    removeItem(index: number) {
      const items = store.items();
      patchState(store, { items: [...items.slice(0, index), ...items.slice(index + 1)] });
      localStorage.setItem(localStorageKey, JSON.stringify(store.items()));
    },

    increaseItemQuantity(index: number) {
      const items = store.items().map((x, i) => (index === i ? { ...x, quantity: x.quantity + 1 } : x));
      patchState(store, { items: items });
      localStorage.setItem(localStorageKey, JSON.stringify(store.items()));
    },

    decreaseItemQuantity(index: number) {
      const items = store.items().map((x, i) => {
        if (index === i && x.quantity > 1) {
          return { ...x, quantity: x.quantity - 1 };
        }
        return x;
      });
      patchState(store, { items: items });
      localStorage.setItem(localStorageKey, JSON.stringify(store.items()));
    },

    reset() {
      patchState(store, { items: [], address: undefined, placeOrderState: initialState(), idempotencyKey: v4() });
      localStorage.removeItem(localStorageKey);
    },

    init() {
      const items = localStorage.getItem(localStorageKey);
      if (items) {
        try {
          patchState(store, { items: JSON.parse(items) });
        } catch (e) {
          console.error(e);
          localStorage.removeItem(localStorageKey);
        }
      }
    },
  })),
);
